import React, { useState, useEffect } from 'react';
import { Mail, Linkedin, Github, FileText, Send, CheckCircle, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const templateParams = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      };
      console.log(templateParams);
      
      const response = await emailjs.send(
        'service_69a4fep',
        'template_41vyyyw',
        templateParams,
        '0pAY8VsjbK0HncBco'
      );
      
      console.log('Email sent successfully:', response);
      
      setFormStatus({
        submitted: true,
        success: true,
        message: 'Thanks for your message! I\'ll get back to you soon.'
      });
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Email sending failed:', error);
      
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Oops! Something went wrong. Please try again later or contact me directly via email.'
      });
    } finally {
      setIsSubmitting(false);
      
      // Reset form status after 5 seconds
      setTimeout(() => {
        setFormStatus({
          submitted: false,
          success: false,
          message: ''
        });
      }, 5000);
    }
  };
  
  useEffect(() => {
    emailjs.init("0pAY8VsjbK0HncBco");
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Get In Touch</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Interested in working together? Feel free to reach out for collaborations, project inquiries, or just to say hello.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Contact Info</h2>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center mb-2">
                    <div className="p-2 bg-blue-100 rounded-full mr-3">
                      <Mail className="text-blue-600" size={18} />
                    </div>
                    <h3 className="font-semibold text-gray-700">Email</h3>
                  </div>
                  <a 
                    href="mailto:pcrilley06@gmail.com" 
                    className="text-blue-600 hover:underline ml-10"
                  >
                    pcrilley06@gmail.com
                  </a>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <div className="p-2 bg-blue-100 rounded-full mr-3">
                      <Linkedin className="text-blue-600" size={18} />
                    </div>
                    <h3 className="font-semibold text-gray-700">LinkedIn</h3>
                  </div>
                  <a 
                    href="https://www.linkedin.com/in/philip-crilley-763199129/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline ml-10"
                  >
                    linkedin.com/in/philip-crilley-763199129
                  </a>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <div className="p-2 bg-blue-100 rounded-full mr-3">
                      <Github className="text-blue-600" size={18} />
                    </div>
                    <h3 className="font-semibold text-gray-700">GitHub</h3>
                  </div>
                  <a 
                    href="https://github.com/philip06" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline ml-10"
                  >
                    github.com/philip06
                  </a>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <div className="p-2 bg-blue-100 rounded-full mr-3">
                      <FileText className="text-blue-600" size={18} />
                    </div>
                    <h3 className="font-semibold text-gray-700">Resume</h3>
                  </div>
                  <a 
                    href="/Philip_Crilley_Resume.pdf" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline ml-10"
                  >
                    Download Resume
                  </a>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-gray-700 text-sm">
                  Currently available for freelance projects or contract work.
                </p>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="md:col-span-2 bg-white p-8 rounded-xl shadow-md">
              {formStatus.submitted ? (
                <div className={`flex items-center p-4 rounded-lg ${
                  formStatus.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {formStatus.success ? (
                    <CheckCircle className="mr-3" />
                  ) : (
                    <AlertCircle className="mr-3" />
                  )}
                  <p>{formStatus.message}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label 
                        htmlFor="name" 
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="John Doe"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>
                    
                    <div>
                      <label 
                        htmlFor="email" 
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Your Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="john@example.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label 
                      htmlFor="subject" 
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                        errors.subject ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Project Inquiry"
                    />
                    {errors.subject && (
                      <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                    )}
                  </div>
                  
                  <div className="mb-6">
                    <label 
                      htmlFor="message" 
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Tell me about your project or inquiry..."
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full ${isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white px-6 py-3 rounded-lg font-semibold shadow-md transition duration-300 flex items-center justify-center`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'} <Send size={18} className="ml-2" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;